import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyAddressUrl,
  usePropertyField,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram } from '@fortawesome/free-brands-svg-icons';
import {
  condenseOfficeHours,
  type OfficeHourEntry,
} from './PropertyInfoPanel';

// ============================================================================
// Local helpers
// ============================================================================

/**
 * Remove a trailing country segment (USA / U.S.A. / United States [of America])
 * plus any leading comma/whitespace. Keeps the visible address focused on the
 * street/city/state on the marketing page.
 */
function stripCountry(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .replace(/\s*,?\s*(u\.?\s*s\.?\s*a\.?|united\s+states(\s+of\s+america)?)\s*$/i, '')
    .trim();
}

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_place_url',
      suggestedFields: ['property_google_place_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const title = useContentToken('contactform.title');
  const addressLabel = useContentToken('contactform.address_label');
  const hoursLabel = useContentToken('contactform.hours_label');

  const addressLink = useSlotGroup('contactform.address', addressLinkDef);
  const facebook = useSlotGroup('contactform.facebook_link', facebookLinkDef);
  const instagram = useSlotGroup('contactform.instagram_link', instagramLinkDef);

  const propertyAddressUrl = usePropertyAddressUrl();
  const propertyAddress = usePropertyField('property_full_address') as
    | string
    | null
    | undefined;

  const officeHours = useCollectionBinding<OfficeHourEntry>(
    'contactform.office_hours',
    { collection: 'office_hours' },
  );

  const formSlot = useBlockSlot('contactform.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  const addressText = stripCountry(addressLink.text || propertyAddress || '');
  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const hoursRows =
    officeHours.length > 0 ? condenseOfficeHours(officeHours) : [];
  const hasSocial = Boolean(facebook.url || instagram.url);

  return (
    <section className="cf-section">
      <div className="cf-container">
        <div className="cf-grid">
          {/* Left column: Address, Hours, Socials */}
          <aside className="cf-info-col">
            <EditableToken
              path="contactform.address_label"
              as="h3"
              className="cf-info-heading"
            >
              {addressLabel || 'Address'}
            </EditableToken>
            <EditableSlotGroup
              groupPath="contactform.address"
              definition={addressLinkDef}
            >
              {addressLink.url || propertyAddressUrl ? (
                <a
                  href={addressHref}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="cf-info-text cf-info-link"
                >
                  {addressText}
                </a>
              ) : (
                <span className="cf-info-text">{addressText}</span>
              )}
            </EditableSlotGroup>

            {hoursRows.length > 0 && (
              <>
                <EditableToken
                  path="contactform.hours_label"
                  as="h3"
                  className="cf-info-heading cf-info-heading--spaced"
                >
                  {hoursLabel || 'Hours'}
                </EditableToken>
                <div className="cf-hours">
                  {hoursRows.map((row, i) => (
                    <div key={i} className="cf-hours-row">
                      <span className="cf-hours-day">{row.dayLabel}</span>
                      <span className="cf-hours-time">{row.timeLabel}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className={`cf-socials${hasSocial ? '' : ' cf-socials--empty'}`}>
              <EditableSlotGroup
                groupPath="contactform.facebook_link"
                definition={facebookLinkDef}
              >
                {facebook.url ? (
                  <a
                    href={facebook.url}
                    target={facebook._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={facebook.label || 'Facebook'}
                    className="cf-social-link"
                  >
                    <FontAwesomeIcon icon={faFacebookF} />
                  </a>
                ) : (
                  <span className="cf-social-link cf-social-link--placeholder" aria-hidden="true">
                    <FontAwesomeIcon icon={faFacebookF} />
                  </span>
                )}
              </EditableSlotGroup>
              <EditableSlotGroup
                groupPath="contactform.instagram_link"
                definition={instagramLinkDef}
              >
                {instagram.url ? (
                  <a
                    href={instagram.url}
                    target={instagram._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={instagram.label || 'Instagram'}
                    className="cf-social-link"
                  >
                    <FontAwesomeIcon icon={faInstagram} />
                  </a>
                ) : (
                  <span className="cf-social-link cf-social-link--placeholder" aria-hidden="true">
                    <FontAwesomeIcon icon={faInstagram} />
                  </span>
                )}
              </EditableSlotGroup>
            </div>
          </aside>

          {/* Right column: title + form */}
          <div className="cf-form-col">
            <EditableToken
              path="contactform.title"
              as="h2"
              className="cf-title"
            >
              {title || 'Contact Us'}
            </EditableToken>

            <EditableToken
              path="contactform.form_block"
              as="div"
              className="cf-form-wrap"
            >
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <p className="cf-form-empty">No form block selected</p>
              )}
            </EditableToken>
          </div>
        </div>
      </div>

      <style>{`
        .cf-section {
          background: white;
          padding: 3rem 1.5rem;
        }
        @media (min-width: 768px) {
          .cf-section { padding: 5rem 2rem; }
        }
        .cf-container {
          max-width: 76rem;
          margin: 0 auto;
        }
        .cf-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 900px) {
          .cf-grid {
            grid-template-columns: minmax(220px, 1fr) minmax(0, 2fr);
            gap: 4rem;
            align-items: start;
          }
        }

        /* ── Info column ── */
        .cf-info-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        @media (min-width: 900px) {
          .cf-info-col {
            align-items: flex-start;
            text-align: left;
          }
        }
        .cf-info-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--vibe-color-primary);
          line-height: 1.2;
          margin: 0 0 1rem;
        }
        .cf-info-heading--spaced {
          margin-top: 2.25rem;
        }
        .cf-info-text {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.6;
          color: var(--vibe-color-text);
          text-decoration: none;
        }
        .cf-info-link {
          transition: color 0.2s ease;
        }
        .cf-info-link:hover,
        .cf-info-link:focus-visible {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        /* ── Hours list ── */
        .cf-hours {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          align-items: center;
        }
        @media (min-width: 900px) {
          .cf-hours { align-items: stretch; }
        }
        .cf-hours-row {
          display: flex;
          align-items: baseline;
          gap: 1.5rem;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
        }
        .cf-hours-day {
          min-width: 6rem;
        }
        @media (max-width: 899.98px) {
          .cf-hours-day {
            min-width: 0;
            text-align: right;
          }
          .cf-hours-time {
            text-align: left;
          }
        }
        .cf-hours-time {
          color: var(--vibe-color-text);
        }

        /* ── Social row ── */
        .cf-socials {
          margin-top: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
        }
        @media (min-width: 900px) {
          .cf-socials { justify-content: flex-start; }
        }
        .cf-socials--empty {
          /* keep the row present (both wrappers must always render for the editor)
             but out of the way when neither URL is set */
          margin-top: 1rem;
          opacity: 0.35;
        }
        .cf-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: var(--vibe-color-primary);
          font-size: 1.5rem;
          line-height: 1;
          transition: opacity 0.2s ease, transform 0.2s ease;
          text-decoration: none;
        }
        .cf-social-link:hover,
        .cf-social-link:focus-visible {
          opacity: 0.75;
          transform: translateY(-1px);
        }
        .cf-social-link--placeholder {
          cursor: default;
        }

        /* ── Form column ── */
        .cf-form-col {
          text-align: center;
        }
        @media (min-width: 900px) {
          .cf-form-col { text-align: left; }
        }
        .cf-title {
          font-family: var(--vibe-font-heading);
          font-size: 2.75rem;
          font-weight: 800;
          color: var(--vibe-color-primary);
          line-height: 1.1;
          letter-spacing: -0.01em;
          margin: 0 0 2rem;
        }
        @media (min-width: 768px) {
          .cf-title { font-size: 3.5rem; }
        }
        .cf-form-wrap {
          width: 100%;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }

        /* ──────────────────────────────────────────────────────────────
           Submit button — matches the site-wide .site-cta--secondary
           treatment used elsewhere (e.g. FloorplansList "View
           Availability"): pill shape, uppercase tracked label, primary
           background that swaps to secondary on hover. The multihub-lead-form
           block lives in a read-only package and applies inline styles to
           its submit button, so these rules use !important to override
           only the visual properties (padding, radius, border, font,
           background, transition) while preserving the block's layout
           behavior (width, disabled state, spinner alignment).
           ────────────────────────────────────────────────────────────── */
        .cf-form-wrap .mh-lead-form-submit-button {
          padding: 1rem 2rem !important;
          min-width: 11rem;
          border-radius: 999px !important;
          border: 2px solid var(--vibe-color-primary) !important;
          background-color: var(--vibe-color-primary) !important;
          color: var(--vibe-color-text-on-primary, #ffffff) !important;
          font-family: var(--vibe-font-body, inherit) !important;
          font-size: 0.78rem !important;
          font-weight: 700 !important;
          letter-spacing: 0.16em;
          line-height: 1;
          text-transform: uppercase;
          transition:
            background-color 0.25s ease,
            color 0.25s ease,
            border-color 0.25s ease,
            transform 0.2s ease,
            box-shadow 0.25s ease !important;
        }
        .cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled) {
          background-color: var(--vibe-color-secondary) !important;
          border-color: var(--vibe-color-secondary) !important;
          color: var(--vibe-color-text-on-secondary, #ffffff) !important;
          transform: translateY(-2px);
        }
        .cf-form-wrap .mh-lead-form-submit-button:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 3px;
        }
        @media (prefers-reduced-motion: reduce) {
          .cf-form-wrap .mh-lead-form-submit-button,
          .cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled) {
            transform: none;
            transition:
              background-color 0.25s ease,
              color 0.25s ease,
              border-color 0.25s ease !important;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactform.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Contact Us',
    },
    'contactform.address_label': {
      label: 'Address Heading',
      type: 'text',
      defaultValue: 'Address',
    },
    'contactform.hours_label': {
      label: 'Hours Heading',
      type: 'text',
      defaultValue: 'Hours',
    },
  },
  slotGroups: {
    'contactform.address': addressLinkDef,
    'contactform.facebook_link': facebookLinkDef,
    'contactform.instagram_link': instagramLinkDef,
  },
  collectionBindings: {
    'contactform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
